import { Link, NavLink, useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import {
  AirplaneTilt,
  Bell,
  CaretDown,
  ChatCircleDots,
  Compass,
  Crown,
  Gear,
  Heart,
  MapTrifold,
  Scales,
  ShieldCheck,
  ShieldStar,
  ShieldWarning,
  SignOut,
  UserCircle,
} from "@phosphor-icons/react";
import { useAuth } from "@/context/AuthContext";
import { api } from "@/lib/api";
import { isRestricted } from "@/constants/ageContract";
import {
  Avatar,
  AvatarFallback,
  AvatarImage,
} from "@/components/ui/avatar";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import Logo from "@/components/Logo";

const APP_NAV = [
  { to: "/entdecken", label: "Entdecken", icon: Compass },
  { to: "/reisen", label: "Reisen", icon: MapTrifold },
  { to: "/reisen-buchen", label: "Reisen buchen", icon: AirplaneTilt },
  { to: "/matches", label: "Matches", icon: Heart },
  { to: "/nachrichten", label: "Nachrichten", icon: ChatCircleDots },
  { to: "/profil", label: "Profil", icon: UserCircle },
];

const DARK_TOKENS = {
  "--foreground": "0 0% 100%",
  "--muted": "215 28% 17%",
  "--muted-foreground": "215 15% 75%",
  "--border": "218 30% 24%",
  "--secondary": "180 72% 48%",
  "--secondary-foreground": "222 60% 9%",
  "--premium": "42 95% 55%",
  "--accent": "187 55% 20%",
  "--accent-foreground": "180 72% 64%",
};

function AppLink({ item }) {
  const Icon = item.icon;

  return (
    <NavLink
      to={item.to}
      className={({ isActive }) =>
        `flex items-center gap-2 rounded-full px-3.5 py-2 text-sm font-semibold transition ${
          isActive
            ? "bg-[#35c8c8]/15 text-[#70e0df]"
            : "text-white/70 hover:bg-white/10 hover:text-white"
        }`
      }
    >
      <Icon size={19} weight="bold" />
      <span>{item.label}</span>
    </NavLink>
  );
}

export default function UnifiedHeader() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const authenticated = Boolean(user);
  const restricted = authenticated && isRestricted(user);
  const showAdmin = Boolean(
    user && (user.is_admin || user.is_superuser || user.role === "admin")
  );

  const { data: unread = 0 } = useQuery({
    queryKey: ["unreadCount"],
    queryFn: async () =>
      (await api.get("/notifications/unread-count")).data.count,
    refetchInterval: 60000,
    refetchOnWindowFocus: true,
    retry: false,
    enabled: authenticated && !restricted,
  });

  const handleLogout = async () => {
    await logout();
    navigate("/", { replace: true });
  };

  return (
    <header
      style={DARK_TOKENS}
      className="sticky top-0 z-50 border-b border-white/10 bg-[#07101d]/95 text-white shadow-lg shadow-black/10 backdrop-blur-xl"
    >
      <div className="mx-auto flex h-16 w-full max-w-[1600px] items-center justify-between gap-2 px-3 sm:gap-3 sm:px-4 2xl:max-w-[1840px]">
        <Link
          to={authenticated ? "/entdecken" : "/"}
          aria-label="Voyalyo Startseite"
          className="shrink-0 [&_img]:max-h-10 [&_img]:max-w-[120px] sm:[&_img]:max-h-none sm:[&_img]:max-w-none"
        >
          <Logo />
        </Link>

        {authenticated ? (
          <nav className="hidden items-center gap-1 lg:flex">
            {!restricted &&
              APP_NAV.map((item) => <AppLink key={item.to} item={item} />)}
          </nav>
        ) : (
          <nav className="hidden items-center gap-7 text-sm font-semibold text-white/75 lg:flex">
            <a href="/reisepartner-finden" className="transition hover:text-white">
              Reisepartner finden
            </a>
            <a href="/#moeglichkeiten" className="transition hover:text-white">
              Möglichkeiten
            </a>
            <a href="/#reiseideen" className="transition hover:text-white">
              Reiseideen
            </a>
            <a href="/#sicherheit" className="transition hover:text-white">
              Sicherheit
            </a>
          </nav>
        )}

        {authenticated ? (
          <div className="flex items-center gap-1">
            {showAdmin && (
              <button
                onClick={() => navigate("/admin")}
                className="hidden rounded-full p-2 text-white/70 transition hover:bg-white/10 hover:text-white lg:block"
                title="Administration"
              >
                <ShieldStar size={22} weight="bold" />
              </button>
            )}

            {!restricted && (
              <>
                <button
                  onClick={() => navigate("/premium")}
                  className="rounded-full p-2 text-amber-400 transition hover:bg-amber-400/10"
                  title="Voyalyo Pro"
                  aria-label="Voyalyo Pro"
                >
                  <Crown size={22} weight={user?.premium ? "fill" : "bold"} />
                </button>

                <button
                  onClick={() => navigate("/benachrichtigungen")}
                  className="relative rounded-full p-2 text-white/70 transition hover:bg-white/10 hover:text-white"
                  title="Benachrichtigungen"
                  aria-label="Benachrichtigungen"
                >
                  <Bell size={22} weight="bold" />
                  {unread > 0 && (
                    <span className="absolute -right-0.5 -top-0.5 flex h-5 min-w-5 items-center justify-center rounded-full bg-[#35c8c8] px-1 text-[11px] font-bold text-[#07101d]">
                      {unread}
                    </span>
                  )}
                </button>
              </>
            )}

            <button
              onClick={() => navigate("/hilfe-sicherheit")}
              className="hidden rounded-full p-2 text-white/70 transition hover:bg-white/10 hover:text-white lg:block"
              title="Hilfe & Sicherheit"
            >
              <ShieldCheck size={22} weight="bold" />
            </button>

            <button
              onClick={() => navigate("/rechtliches")}
              className="hidden rounded-full p-2 text-white/70 transition hover:bg-white/10 hover:text-white lg:block"
              title="Rechtliches"
            >
              <Scales size={22} weight="bold" />
            </button>

            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button
                  title="Profilmenü"
                  aria-label="Profilmenü öffnen"
                  className="flex items-center gap-1 rounded-full outline-none transition hover:opacity-90"
                >
                  <Avatar className="h-9 w-9 border-2 border-white/20">
                    <AvatarImage src={user?.photoURL} />
                    <AvatarFallback className="bg-[#143945] font-semibold text-[#70e0df]">
                      {user?.displayName?.[0] || "?"}
                    </AvatarFallback>
                  </Avatar>
                  <CaretDown
                    size={14}
                    weight="bold"
                    className="text-white/60"
                  />
                </button>
              </DropdownMenuTrigger>

              <DropdownMenuContent align="end" className="w-56">
                <DropdownMenuLabel className="truncate">
                  {user?.displayName || "Mein Konto"}
                </DropdownMenuLabel>
                <DropdownMenuSeparator />

                <DropdownMenuItem onClick={() => navigate("/profil")}>
                  <UserCircle size={18} weight="bold" className="mr-2" />
                  Profil
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => navigate("/einstellungen")}>
                  <Gear size={18} weight="bold" className="mr-2" />
                  Einstellungen
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => navigate("/guide")}>
                  <Compass size={18} weight="bold" className="mr-2" />
                  Voyalyo kennenlernen
                </DropdownMenuItem>
                <DropdownMenuItem
                  onClick={() => navigate("/altersverifikation")}
                >
                  <ShieldWarning size={18} weight="bold" className="mr-2" />
                  Verifizierung
                </DropdownMenuItem>

                {!restricted && (
                  <DropdownMenuItem onClick={() => navigate("/premium")}>
                    <Crown size={18} weight="bold" className="mr-2" />
                    Voyalyo Pro
                  </DropdownMenuItem>
                )}

                {showAdmin && (
                  <DropdownMenuItem onClick={() => navigate("/admin")}>
                    <ShieldStar size={18} weight="bold" className="mr-2" />
                    Administration
                  </DropdownMenuItem>
                )}

                {!restricted && (
                  <DropdownMenuItem onClick={() => navigate("/reisen-buchen")}>
                    <AirplaneTilt size={18} weight="bold" className="mr-2" />
                    Reisen buchen
                  </DropdownMenuItem>
                )}
                <DropdownMenuItem onClick={() => navigate("/hilfe-sicherheit")}>
                  <ShieldCheck size={18} weight="bold" className="mr-2" />
                  Hilfe & Sicherheit
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => navigate("/rechtliches")}>
                  <Scales size={18} weight="bold" className="mr-2" />
                  Rechtliches
                </DropdownMenuItem>

                <DropdownMenuSeparator />
                <DropdownMenuItem
                  onClick={handleLogout}
                  className="text-destructive focus:bg-destructive/10 focus:text-destructive"
                >
                  <SignOut size={18} weight="bold" className="mr-2" />
                  Abmelden
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        ) : (
          <div className="flex items-center gap-2 sm:gap-3">
            <Link
              to="/login"
              className="rounded-full px-3 py-2 text-sm font-bold text-white/85 transition hover:bg-white/10 hover:text-white sm:px-5"
            >
              Anmelden
            </Link>
            <Link
              to="/registrieren"
              className="rounded-full bg-[#35c8c8] px-4 py-2.5 text-sm font-extrabold text-[#07101d] transition hover:bg-[#54d5d5] sm:px-5"
            >
              Kostenlos starten
            </Link>
          </div>
        )}
      </div>
    </header>
  );
}
